import { ExtendedGridColDef, DatagridNames } from 'types/datagrid';
import ExtendedDatagrid from 'ui-component/DataGrid/ExtendedDatagrid';
import { GridCellParams, GridValueGetterParams } from '@mui/x-data-grid';
import _ from 'lodash';
import { longTextWithEllipsisRenderCell } from 'ui-component/DataGrid/Render';
import { ALL, DISABLE_PAGE_LIMIT, Lite } from 'types/api';
import { useGetTagsQuery } from 'store/slices/clientV2/tags';
import useServerSideQuery from 'hooks/useServerSideQuery';
import { TagColorOptionsDisplay, TagSchema } from 'types/clientV2/tag';
import useCustomTeamTypeName from 'hooks/useCustomTeamTypeName';
import Chip, { ChipColor } from 'ui-component/extended/Chip';
import { useState, useMemo } from 'react';
import CreateTagDialog from 'views/properties/Tags/TagsTable/CreateTagDialog';
import DeleteTagDialog from 'views/properties/Tags/TagsTable/DeleteTagDialog';
import { IconPencil, IconTrash } from '@tabler/icons-react';
import UpdateTagDialog from 'views/properties/Tags/TagsTable/UpdateTagDialog';
import { COLOR_OPTIONS } from 'types/index';
import { ALL_RECORD_TYPES } from 'constants/recordTypes';

const TagsTable = () => {
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState<TagSchema | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { handleReplaceWithCustomTeamName } = useCustomTeamTypeName();

  const { data: tags, isLoading: isLoadingTags } = useServerSideQuery<
    TagSchema,
    Lite<TagSchema, 'team' | 'modelOptions'>
  >(useGetTagsQuery, {
    pageSize: DISABLE_PAGE_LIMIT,
    schema: [
      ALL,
      {
        team: ['name', 'id'],
        modelOptions: [],
      },
    ],
  });

  const columns = useMemo<ExtendedGridColDef[]>(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        description: 'Tag name',
        type: 'string',
        flex: 1,
        renderCell: (params: GridCellParams) => (
          <Chip
            title={params.value}
            label={params.value}
            chipcolor={params.row.color}
            type="tag"
          />
        ),
      },
      {
        field: 'modelOptions',
        headerName: 'Data Types',
        description: 'Associated data types for the tag',
        type: 'string',
        flex: 1,
        valueGetter: (params: GridValueGetterParams) =>
          params.row.modelOptions
            ? _.chain(params.row.modelOptions)
                .map(
                  (o) =>
                    _.find(ALL_RECORD_TYPES, { flagshipModel: o.model })
                      ?.singularName
                )
                .join(', ')
                .value()
            : 'All Models',
      },
      {
        field: 'color',
        headerName: 'Color',
        description: 'Color of the tag',
        type: 'singleSelect',
        flex: 1,
        options: Object.values(TagColorOptionsDisplay),
        valueGetter: (params: GridValueGetterParams) =>
          TagColorOptionsDisplay[params.row.color as ChipColor],
      },
      {
        field: 'active',
        headerName: 'Active',
        description: 'Whether the tag is active',
        type: 'boolean',
        flex: 0.5,
      },
      {
        field: 'team',
        headerName: handleReplaceWithCustomTeamName('Team'),
        description: handleReplaceWithCustomTeamName(
          'Team associated with this tag'
        ),
        valueGetter: (params: GridValueGetterParams) =>
          params.row.team?.name ?? '-',
        renderCell: longTextWithEllipsisRenderCell,
      },
    ],
    [handleReplaceWithCustomTeamName]
  );

  const rowActions = [
    {
      Icon: IconPencil,
      color: COLOR_OPTIONS.primary,
      getLabel: () => 'Edit',
      onRowClick: (row?: TagSchema) => {
        if (row) {
          setSelectedTag(row);
          setEditOpen(true);
        }
      },
    },
    {
      Icon: IconTrash,
      color: COLOR_OPTIONS.error,
      getLabel: () => 'Delete',
      onRowClick: (row?: TagSchema) => {
        if (row) {
          setSelectedTag(row);
          setDeleteOpen(true);
        }
      },
    },
  ];

  return (
    <>
      <ExtendedDatagrid
        fullCard
        loading={isLoadingTags}
        noRows="No Tags found."
        newRecordButton={{
          label: 'Create Tag',
          onClick: () => setCreateOpen(true),
        }}
        gridName={DatagridNames.tags}
        rows={tags?.data ?? []}
        rowActions={rowActions}
        columns={columns}
        initialVisibleColumns={[
          'name',
          'modelOptions',
          'color',
          'active',
          'team',
        ]}
      />
      <CreateTagDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      {selectedTag && (
        <UpdateTagDialog
          open={editOpen}
          tag={selectedTag}
          onClose={() => setEditOpen(false)}
        />
      )}
      <DeleteTagDialog
        open={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setSelectedTag(null);
        }}
        tag={selectedTag}
      />
    </>
  );
};

export default TagsTable;
